import api from '../../API';
import { girderThumbnailUrl } from '../../utils/girderThumbnailUrl';
import styles from './ProjectItemListWithProgress.module.css';

/**
 * Renders a list of project items with progress badges (Not started / In progress / Completed).
 * Used in the viewer item dropdown and on the project splash "Your progress" section.
 *
 * @param {Object} props
 * @param {Array<{ _id: string, name: string }>} props.items - Project items in display order
 * @param {Object<string, string>} props.progressMap - Map itemId -> status ('not_started' | 'in_progress' | 'completed')
 * @param {(item: Object) => void} props.onSelectItem - Called when a row is clicked
 * @param {string | null} [props.currentItemId] - If set, the row with this id gets the active style (e.g. viewer dropdown)
 * @param {boolean} [props.showProgressBadge=true] - When false, do not show the status badge (e.g. in read-only review viewer)
 */
export default function ProjectItemListWithProgress({
    items,
    progressMap = {},
    onSelectItem,
    currentItemId = null,
    showProgressBadge = true,
}) {
    return (
        <div className={styles.list} role="listbox">
            {items.map((item, idx) => {
                const iid = String(item._id);
                const isActive = currentItemId != null && iid === String(currentItemId);
                const status = showProgressBadge ? (progressMap[iid] || 'not_started') : null;
                const completedStatus = status === 'completed';
                const inProgressStatus = status === 'in_progress';
                let label = 'Not started';
                let badgeClass = styles.progressBadgeNotStarted;
                let icon = null;
                if (showProgressBadge) {
                    if (completedStatus) {
                        label = 'Completed';
                        badgeClass = styles.progressBadgeCompleted;
                        icon = (
                            <svg className={styles.progressBadgeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M5 13l4 4L19 7" />
                            </svg>
                        );
                    } else if (inProgressStatus) {
                        label = 'In progress';
                        badgeClass = styles.progressBadgeInProgress;
                        icon = (
                            <svg className={styles.progressBadgeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" />
                            </svg>
                        );
                    }
                }
                return (
                    <button
                        key={iid}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        className={`${styles.itemRow} ${isActive ? styles.itemRowActive : ''}`}
                        onClick={() => onSelectItem(item)}
                    >
                        <span className={styles.itemIndex}>{idx + 1}</span>
                        <img
                            src={girderThumbnailUrl(api, iid)}
                            alt=""
                            className={styles.itemThumb}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className={styles.itemName} title={item.name}>
                            {item.name}
                        </span>
                        {showProgressBadge && (
                            <span className={`${styles.itemProgress} ${badgeClass}`}>
                                {icon}
                                {label}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
